import type { ReactNode } from 'react';

/**
 * `.ledger` + `.ledger-row` — the canonical PCO/rollup row vocabulary.
 * Used on Dashboard's "Active PCOs this week" panel and on the full PCO
 * list page. Two-column grid: left main (id/name/meta/burn), right side
 * (amount/status).
 *
 * Components are split fine-grained so callers can compose:
 *   <Ledger>
 *     <LedgerRow onClick={...}>
 *       <LedgerMain>
 *         <LedgerLine1>
 *           <LedgerId>PCO 004.6</LedgerId>
 *           <LedgerName>South Utilities Package</LedgerName>
 *         </LedgerLine1>
 *         <LedgerMeta>MOD 46975-004 · WA 124-006</LedgerMeta>
 *         <Burn pct={72} />
 *       </LedgerMain>
 *       <LedgerSide>
 *         <LedgerAmount>$303,840 of $420,000</LedgerAmount>
 *         <StatusPill kind="progress">Active · 72%</StatusPill>
 *       </LedgerSide>
 *     </LedgerRow>
 *   </Ledger>
 *
 * Money + IDs in mono, names in serif. The design enforces this via the
 * .lr-* classes — these wrappers just emit them.
 */
export function Ledger({ children }: { children: ReactNode }) {
  return <div className="ledger">{children}</div>;
}

export function LedgerRow({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      className="ledger-row"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </div>
  );
}

export function LedgerMain({ children }: { children: ReactNode }) {
  return <div className="lr-main">{children}</div>;
}

export function LedgerLine1({ children }: { children: ReactNode }) {
  return <div className="lr-line1">{children}</div>;
}

export function LedgerId({ children }: { children: ReactNode }) {
  return <span className="lr-id">{children}</span>;
}

export function LedgerName({ children }: { children: ReactNode }) {
  return <span className="lr-name">{children}</span>;
}

export function LedgerMeta({ children }: { children: ReactNode }) {
  return <div className="lr-meta">{children}</div>;
}

export function LedgerDot() {
  return <span className="dot">·</span>;
}

export function LedgerSide({ children }: { children: ReactNode }) {
  return <div className="lr-side">{children}</div>;
}

export function LedgerAmount({ children }: { children: ReactNode }) {
  return <div className="lr-amount">{children}</div>;
}

/**
 * Hairline progress rule. Width is 320px on Dashboard, 420px on the full
 * PCO list per the design — set `width` inline when overriding. Pct is
 * clamped to [0, 100] so a runaway value doesn't ooze past the bar.
 */
export function Burn({
  pct,
  variant,
  width,
}: {
  pct: number;
  variant?: 'warn' | 'danger';
  width?: number;
}) {
  const safePct = Math.max(0, Math.min(100, pct));
  const cls = ['burn', variant].filter(Boolean).join(' ');
  return (
    <div
      className={cls}
      style={
        {
          ['--pct' as string]: `${safePct}%`,
          ...(width ? { width } : {}),
        } as React.CSSProperties
      }
    />
  );
}

/**
 * `.lr-status` pill — colored leading dot + label. Each kind maps to the
 * design's matching `.s-ready` / `.s-progress` / `.s-warning` / `.s-pending`
 * / `.s-danger` / `.s-void` variant; `neutral` emits the bare pill with no
 * variant class.
 */
export type StatusPillKind =
  | 'ready'
  | 'progress'
  | 'warning'
  | 'pending'
  | 'danger'
  | 'void'
  | 'neutral';

export function StatusPill({
  kind,
  children,
}: {
  kind: StatusPillKind;
  children: ReactNode;
}) {
  const cls = kind === 'neutral' ? 'lr-status' : `lr-status s-${kind}`;
  return <span className={cls}>{children}</span>;
}
